import useLastNonNullValue from '@/Hooks/Helper/UseLastNonNullValue';
import { Label } from '@/Models/GenericWorklist/Label';
import { type WorklistPaginationParams } from '@/Models/GenericWorklist/WorklistParams';
import {
    DobButton,
    DobPagination,
} from '@dedalusdiit/dob-components';
import React, { ReactElement } from 'react';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import Styles from './WorklistFooter.module.scss';
import { WorklistSelectionActions } from './WorklistSelectionActions';
import { IWorklistStore } from '@/Pages/GenericWorklist/Stores/WorklistStore';
import { type WorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';

export interface WorklistFooterProps {
    fullCount: number | null;
    fullCountOverflow: boolean;
    isLoading: boolean;
    paging: WorklistPaginationParams;
    refreshResults: () => void;
    setPaging: (pagingParams: WorklistPaginationParams) => void;
    allLabels: Label[] | [];
    worklistStore: IWorklistStore;
    selectionHelper: WorklistSelectionHelper;
}

const WorklistFooter = observer((props: WorklistFooterProps): ReactElement => {
    const {
        paging,
        fullCount,
        fullCountOverflow,
        setPaging,
        refreshResults,
        isLoading,
        allLabels,
        selectionHelper,
    } = props;

    // To avoid re-renders during loading where the fullcount is temporarily unknown, cache the last non-null value
    const nonLoadingFullCount = useLastNonNullValue(fullCount, 0);

    const currentOffset = paging.Offset;

    const setOffSet = (offset: number): void => {
        setPaging({
            ...paging,
            Offset: offset,
        });
    };
    return (
        <div className={Styles.tableFooter}>
            <div className={Styles.footerActions}>
                <PageSelector disabled={isLoading} fullCount={nonLoadingFullCount} fullCountOverflow={fullCountOverflow} currentOffset={currentOffset} pageSize={paging.PageSize} setOffSet={setOffSet} />
                <div>|</div>
                <div>
                    <DobButton onClick={() => refreshResults()} color="silent">{t('Reload')}</DobButton>
                </div>
            </div>
            <WorklistSelectionActions
                allLabels={allLabels}
                assignLabelToOrders={selectionHelper.assignLabelToOrders}
                handleMarkAsReady={selectionHelper.handleMarkAsReady}
                labelsAssignedToSelectedOrders={selectionHelper.labelsAssignedToSelectedOrders}
                removeLabelFromOrders={selectionHelper.removeLabelFromOrders}
                anySelected={selectionHelper.anySelected}
                worklistStore={props.worklistStore}
            />
        </div>
    );
});

interface PageSelectorProps {
    currentOffset: number;
    disabled?: boolean;
    fullCount: number;
    fullCountOverflow: boolean;
    pageSize: number;
    setOffSet: (offSet: number) => void;
}

const PageSelector = (props: PageSelectorProps): ReactElement => {
    const { fullCount, fullCountOverflow, pageSize, currentOffset, setOffSet, disabled } = props;

    const currentPage = currentOffset / pageSize + 1;

    const changePage = (page: number): void => {
        if (disabled) {
            return;
        }
        setOffSet((page - 1) * pageSize);
    };

    return (
        <div className={Styles.pagination}>
            <DobPagination currentPage={currentPage} recordsPerPage={pageSize} totalRecords={fullCount} onChange={changePage} />
            {fullCountOverflow && <div>+</div> }
        </div>
    );
};

export default WorklistFooter;
